import Cookies from "js-cookie";

import { getCookieDomain } from "~/utils";

import { safeLocalStorage } from "./safeStorage";

// localStorage, общий для всех вкладок: окна перехватчика показываются не чаще
// раза за визит, а отсчёт «3 минут на сайте» начинается заново в каждом визите.
// Визит заканчивается после VISIT_IDLE_MS без переходов по страницам.
const STORAGE_KEY = "interceptorTriggerState";
const VISIT_IDLE_MS = 30 * 60 * 1000;
// localStorage у каждого поддомена свой, поэтому «окно уже показано» дублируем
// в куку на весь домен: показали на spb.farkop.ru — не показываем и на msk.
const SHOWN_COOKIE = "interceptorShown";

const setShownCookie = () => {
  Cookies.set(SHOWN_COOKIE, "1", {
    domain: getCookieDomain(),
    path: "/",
    expires: new Date(Date.now() + VISIT_IDLE_MS),
  });
};

export const INTERCEPTOR_TRIGGER_IDS = {
  pageDepth4: "pageDepth4",
  exitModelCatalog: "exitModelCatalog",
  timeOnSite3min: "timeOnSite3min",
};

export const INTERCEPTOR_TRIGGERS = {
  [INTERCEPTOR_TRIGGER_IDS.pageDepth4]: {
    id: INTERCEPTOR_TRIGGER_IDS.pageDepth4,
    info: "Пытался уйти со страницы или долго бездействовал",
  },
  [INTERCEPTOR_TRIGGER_IDS.exitModelCatalog]: {
    id: INTERCEPTOR_TRIGGER_IDS.exitModelCatalog,
    info: "Пытался закрыть страницу в каталоге",
  },
  [INTERCEPTOR_TRIGGER_IDS.timeOnSite3min]: {
    id: INTERCEPTOR_TRIGGER_IDS.timeOnSite3min,
    info: "Пробыл на сайте 3 минуты",
  },
};

const THREE_MINUTES_MS = 3 * 60 * 1000;

const getDefaultState = () => ({
  sessionStart: Date.now(),
  pages: [],
  triggers: {
    [INTERCEPTOR_TRIGGER_IDS.pageDepth4]: { fired: false },
    [INTERCEPTOR_TRIGGER_IDS.exitModelCatalog]: { fired: false },
    [INTERCEPTOR_TRIGGER_IDS.timeOnSite3min]: { fired: false },
  },
});

const isBrowser = () => typeof window !== "undefined";

const normalizePath = (url) => {
  if (!url) {
    return "";
  }
  return url.split("?")[0].split("#")[0];
};

export const interceptorStorage = {
  read() {
    if (!isBrowser()) {
      return getDefaultState();
    }
    try {
      const data = safeLocalStorage.getItem(STORAGE_KEY);
      if (!data) {
        return getDefaultState();
      }
      const parsed = JSON.parse(data);
      if (!(Date.now() - (parsed?.lastSeen || 0) < VISIT_IDLE_MS)) {
        return getDefaultState();
      }
      return {
        ...getDefaultState(),
        ...parsed,
        triggers: {
          ...getDefaultState().triggers,
          ...(parsed?.triggers || {}),
        },
      };
    } catch (error) {
      return getDefaultState();
    }
  },
  write(state) {
    if (!isBrowser()) {
      return;
    }
    try {
      safeLocalStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, lastSeen: Date.now() }),
      );
    } catch (error) {}
  },
  recordPageView(url) {
    const path = normalizePath(url);
    const state = this.read();
    const lastPath = state.pages?.[state.pages.length - 1]?.path;
    if (path && path !== lastPath) {
      const nextPages = [...(state.pages || []), { path, ts: Date.now() }];
      state.pages = nextPages.slice(-50);
    }
    this.write(state);
    // Переход продлевает визит и на других поддоменах.
    if (Cookies.get(SHOWN_COOKIE)) {
      setShownCookie();
    }
    return state;
  },
  markTriggerFired(triggerId, meta = {}) {
    const state = this.read();
    state.triggers = state.triggers || {};
    state.triggers[triggerId] = {
      ...(state.triggers[triggerId] || {}),
      fired: true,
      firedAt: new Date().toISOString(),
      ...meta,
    };
    state.lastTriggerId = triggerId;
    this.write(state);
    setShownCookie();
    return state;
  },
  hasAnyTriggerFired() {
    if (Cookies.get(SHOWN_COOKIE)) {
      return true;
    }
    const state = this.read();
    return Object.values(state.triggers || {}).some(
      (trigger) => trigger?.fired,
    );
  },
};

export const interceptorPayload = {
  build(triggerId) {
    const state = interceptorStorage.read();
    const trigger = INTERCEPTOR_TRIGGERS[triggerId];
    return {
      triggerId,
      triggerTitle: trigger?.title || "",
      triggerSubtitle: trigger?.subtitle || "",
      firedAt: new Date().toISOString(),
      sessionStart: state.sessionStart,
      pages: state.pages,
      triggers: state.triggers,
      lastTriggerId: state.lastTriggerId || "",
      currentPath:
        (isBrowser() && normalizePath(window.location.pathname)) || "",
    };
  },
  toString(triggerId) {
    try {
      return JSON.stringify(this.build(triggerId));
    } catch (error) {
      return "";
    }
  },
};

export const interceptorEvents = {
  shouldTriggerTimeOnSite(state, now = Date.now()) {
    const triggerState =
      state?.triggers?.[INTERCEPTOR_TRIGGER_IDS.timeOnSite3min];
    if (triggerState?.fired) {
      return false;
    }
    return now - (state?.sessionStart || now) >= THREE_MINUTES_MS;
  },
  getTimeOnSiteDelayMs(state, now = Date.now()) {
    const triggerState =
      state?.triggers?.[INTERCEPTOR_TRIGGER_IDS.timeOnSite3min];
    if (triggerState?.fired) {
      return null;
    }
    const elapsed = now - (state?.sessionStart || now);
    return Math.max(0, THREE_MINUTES_MS - elapsed);
  },
  isExitIntent(event) {
    if (!event) {
      return false;
    }
    // Курсор ушёл за верхний край окна — к вкладкам и кнопке закрытия.
    const isOutside = !event.relatedTarget && !event.toElement;
    return isOutside && event.clientY <= 0;
  },
  isModelCatalogPage(url) {
    const path = normalizePath(url).toLowerCase();

    return (
      path.includes("/catalog/") &&
      path.includes("/filter/") &&
      path.includes("model-")
    );
  },
};
